import { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { API } from "../../App";
import { toast } from "sonner";
import { TestTube, User, Bot, AlertOctagon, CheckCircle, Loader2, Webhook, Send } from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";

export const TestAIDialog = ({ open, onOpenChange }) => {
  const navigate = useNavigate();
  const [testMessage, setTestMessage] = useState("");
  const [testResponse, setTestResponse] = useState(null);
  const [testing, setTesting] = useState(false);
  const [sendingToHubspot, setSendingToHubspot] = useState(false);
  const [hubspotTestResult, setHubspotTestResult] = useState(null);

  const sendTestToHubspot = async () => {
    if (!testResponse?.ai_response) return;
    setSendingToHubspot(true);
    setHubspotTestResult(null);
    try {
      const res = await axios.post(`${API}/webhook/hubspot/test/send`, {
        ai_response: testResponse.ai_response
      }, { withCredentials: true });
      setHubspotTestResult(res.data);
      toast.success(`Sent to HubSpot test thread ${res.data.thread_id}`);
    } catch (error) {
      const detail = error.response?.data?.detail || "Failed to send to HubSpot";
      toast.error(detail, {
        action: detail.includes("Settings") ? { label: "Open Settings", onClick: () => navigate("/settings") } : undefined
      });
    } finally {
      setSendingToHubspot(false);
    }
  };

  const runTest = async () => {
    if (!testMessage.trim()) {
      toast.error("Please enter a test message");
      return;
    }

    setTesting(true);
    setTestResponse(null);
    setHubspotTestResult(null);

    try {
      const response = await axios.post(`${API}/webhook/hubspot/test`, {
        message: testMessage
      }, { withCredentials: true });
      
      setTestResponse(response.data);
      toast.success("Test completed!");
    } catch (error) {
      toast.error("Test failed");
    } finally {
      setTesting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <TestTube className="w-5 h-5 text-violet-500" />
                Test AI Response
              </DialogTitle>
              <DialogDescription>
                Test how the AI would respond to a customer message without sending to HubSpot
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div>
                <label className="text-sm font-medium text-slate-700">Simulated Customer Message</label>
                <Textarea
                  value={testMessage}
                  onChange={(e) => setTestMessage(e.target.value)}
                  placeholder="e.g., How do I submit an invoice?"
                  className="mt-1"
                  rows={3}
                />
              </div>

              {testResponse && (
                <div className="space-y-3 p-4 bg-slate-50 rounded-lg">
                  <div className="flex items-start gap-2">
                    <User className="w-4 h-4 text-slate-500 mt-0.5" />
                    <div>
                      <p className="text-xs font-medium text-slate-500">Customer</p>
                      <p className="text-sm">{testResponse.input_message}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <Bot className="w-4 h-4 text-violet-500 mt-0.5" />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="text-xs font-medium text-violet-600">AI Response</p>
                        {testResponse.needs_escalation && (
                          <Badge variant="outline" className="text-[10px] border-red-200 text-red-600 bg-red-50" data-testid="escalation-badge">
                            <AlertOctagon className="w-3 h-3 mr-1" />
                            Will Escalate to Human
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm whitespace-pre-wrap" data-testid="test-ai-response-text">{testResponse.ai_response}</p>
                    </div>
                  </div>
                  {hubspotTestResult && (
                    <div className="flex items-start gap-2 pt-2 border-t border-slate-200" data-testid="hubspot-test-sent">
                      <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5" />
                      <p className="text-xs text-slate-600">
                        Sent to HubSpot thread {hubspotTestResult.thread_id}.{" "}
                        {hubspotTestResult.thread_link && (
                          <a href={hubspotTestResult.thread_link} target="_blank" rel="noreferrer" className="text-blue-600 underline" data-testid="hubspot-thread-link">
                            Open in HubSpot Inbox
                          </a>
                        )}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>

            <DialogFooter className="flex-wrap gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Close
              </Button>
              {testResponse?.ai_response && (
                <Button
                  variant="outline"
                  onClick={sendTestToHubspot}
                  disabled={sendingToHubspot}
                  className="gap-2 border-sky-200 text-sky-700 hover:bg-sky-50"
                  data-testid="send-test-to-hubspot-btn"
                >
                  {sendingToHubspot ? <Loader2 className="w-4 h-4 animate-spin" /> : <Webhook className="w-4 h-4" />}
                  Send test to HubSpot
                </Button>
              )}
              <Button 
                onClick={runTest} 
                disabled={testing}
                className="gap-2 bg-violet-600 hover:bg-violet-700"
              >
                {testing ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
                Test Response
              </Button>
            </DialogFooter>
          </DialogContent>
    </Dialog>
  );
};
